import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AdminDiagnostics from './app/pages/AdminDiagnostics.js';
import 'antd/dist/reset.css';

import PublicShell from './app/shell/PublicShell.js';
import HomePage from './app/pages/HomePage.js';
import ScannerPage from './app/pages/ScannerPage.js';
import HealthPage from './app/pages/HealthPage.js';
import { SitesPage } from './app/sites/SitesPage.js';
import { ExportsPage } from './app/exports/ExportsPage.js';
import { CookieOverridesPage } from './app/overrides/CookieOverridesPage.js';
import AnalyticsPage from './app/pages/AnalyticsPage.js';
import { ScansPage } from './app/scans/ScansPage.js';
import ConfigList from './app/config/ConfigList.js';
import { usePortalCtx } from './app/context.js';
import { useAuthBootstrap } from './app/auth-bootstrap.js';
import { PortalProviders } from './app/providers.js';
import { ErrorBoundary } from './app/routes/ErrorBoundary.js';
import { PortalI18nProvider } from './app/i18n.js';
import { Protected } from './app/routes/Protected.js';
import PublicOnly from './app/routes/PublicOnly.js';
import LoginRedirect from './app/auth/LoginRedirect.js';
import AppShell from './app/shell/AppShell.js';
import { Register } from '@digiwedge/ui-antd';
import { Require } from './app/rbac/Require.js';
import Forbidden from './app/routes/Forbidden.js';
import NotFound from './app/routes/NotFound.js';
import { tour as tourCore } from '@digiwedge/tour-core';
import {
  installTcfCoreApiOnWindow,
  setCoreModel,
  setPublisherModel,
  type CoreModel,
  type PurposeId,
  type PublisherModel,
} from '@digiwedge/cmp-sdk-web';
import { setUsStateConfig } from '@digiwedge/cmp-sdk-web';
import { LocalStorageTourStorage } from '@digiwedge/tour-core';
import { collectAndEmitConsent } from './consent/emit.js';
import { DomPortalForTour } from '@digiwedge/tour-react';
import { PreviewBadge } from './tours/PreviewBadge.js';
import DevFlagsBanner from './app/ui/DevFlagsBanner.js';
import { initRegion, getRegion, type Region } from './region/RegionService.js';
import { createEventEmitter, type TourEventIngest } from '@digiwedge/tour-client';
import { ExperimentsEmitter } from '@digiwedge/experiments-client';
import { useProfile } from '@digiwedge/hooks-auth-web';
import { makeCmpConsentProvider } from '@digiwedge/tour-core';
import { maybeRunTour } from './tours/runtime.js';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { FLAGS } from './config/flags.js';
import { ThemeEditorPage } from './app/pages/themes/index.js';

function AppBoot({ children }: { children: React.ReactNode }) {
  useAuthBootstrap();
  return children as JSX.Element;
}

function SitesPageWrapper() {
  const { registryUrl, cdnUrl } = usePortalCtx();
  return <SitesPage registryUrl={registryUrl} cdnUrl={cdnUrl ?? ''} />;
}
function ExportsPageWrapper() {
  const { registryUrl, siteKey } = usePortalCtx();
  return <ExportsPage registryUrl={registryUrl} siteKey={siteKey ?? 'DEV_SITE_KEY'} />;
}
function OverridesPageWrapper() {
  const { registryUrl, siteKey } = usePortalCtx();
  return (
    <Require perm="cmp.admin">
      <CookieOverridesPage registryUrl={registryUrl} siteKey={siteKey ?? 'DEV_SITE_KEY'} />
    </Require>
  );
}
function AnalyticsPageWrapper() {
  return <AnalyticsPage />;
}
function ScansPageWrapper() {
  const { registryUrl } = usePortalCtx();
  return <ScansPage registryUrl={registryUrl} />;
}
function ConfigEditorWrapper() {
  // const { registryUrl, siteKey } = usePortalCtx();
  // Route to Config list; editor remains available at /config/edit
  return <ConfigList />;
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

// Configure persistent storage for tours (tenant/app scoped)
try {
  tourCore.configure({ storage: new LocalStorageTourStorage('portal.') });
} catch {
  // ignore storage errors (SSR or privacy settings)
}

// Engagement API wiring (consent gate + identity from profile)
const im = (import.meta as unknown as { env?: Record<string, string | undefined> }) || {};
const ENG_URL = String(im.env?.VITE_ENGAGEMENT_API_URL || '');

const consentProvider = makeCmpConsentProvider();

// Surface Vite-provided env vars to libraries that read via globalThis.importMetaEnv
try {
  const globalEnv = globalThis as unknown as {
    importMetaEnv?: Record<string, unknown>;
  };
  const metaEnv = (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {};
  globalEnv.importMetaEnv = { ...(globalEnv.importMetaEnv ?? {}), ...metaEnv };
} catch {
  /* noop */
}

// Initialize single-source region early (dev/stage hint via VITE_TCF_REGION)
try {
  const meta = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
  initRegion((meta.VITE_TCF_REGION as string | undefined as Region | undefined) ?? null);
} catch {
  /* noop */
}

// Configure US state GPP rollout per tenant (runtime), falling back to env
try {
  type UsState = 'va' | 'co' | 'ct' | 'ut';
  type TenantGppStateSections = { enabled?: boolean; ids?: Partial<Record<UsState, number>> };
  const w = window as unknown as {
    __cmpTenantConfig?: { gppStateSections?: TenantGppStateSections };
  };
  const tenantCfg: TenantGppStateSections | undefined = w.__cmpTenantConfig?.gppStateSections;
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
  if (tenantCfg) {
    setUsStateConfig({ enabled: tenantCfg.enabled, ids: tenantCfg.ids });
  } else {
    const en = env.VITE_GPP_US_STATES_ENABLE === '1';
    const idsRaw = env.VITE_USSTATE_SECTION_IDS || '';
    const ids: Partial<Record<UsState, number>> = {};
    // Accept CSV like "va:9,co:10" or JSON {"va":9,...}
    if (idsRaw.startsWith('{')) {
      try {
        const parsed = JSON.parse(idsRaw) as Partial<Record<UsState, number>>;
        Object.assign(ids, parsed);
      } catch {
        /* ignore */
      }
    } else if (idsRaw) {
      idsRaw.split(',').forEach((pair) => {
        const [k, v] = pair.split(':').map((s) => s.trim());
        if (
          (['va', 'co', 'ct', 'ut'] as UsState[]).includes(k as UsState) &&
          v &&
          !Number.isNaN(Number(v))
        ) {
          ids[k as UsState] = Number(v);
        }
      });
    }
    setUsStateConfig({ enabled: en, ids });
  }
} catch {
  /* ignore */
}

function useProductAnalyticsConsent(): boolean {
  const [ok, setOk] = useState<boolean>(() => consentProvider.analyticsAllowed());
  useEffect(() => consentProvider.onChange(setOk), []);
  return ok;
}

function TourAnalyticsWire({ consentOk }: { consentOk: boolean }) {
  const { profile, fetchProfile } = useProfile();
  // Fetch profile after mount when missing to avoid render-time loops
  useEffect(() => {
    if (!profile) void fetchProfile();
  }, [profile, fetchProfile]);

  const tenantId = (profile?.tenantIds && profile.tenantIds[0]) || 'default';
  const userId = profile?.userId;
  const token = useGlobalStore((s: GlobalState) => s.token);
  const identity = {
    tenantId,
    userId,
    app: 'cmp-portal',
    platform: 'web',
    locale: (navigator && navigator.language) || 'en',
  } as const;

  const emitter = ENG_URL
    ? createEventEmitter({
        baseUrl: ENG_URL,
        identity,
        isAllowed: () => consentOk,
        fetcher: async (url, init) => {
          const headers = new Headers(init.headers || {});
          if (token) headers.set('Authorization', `Bearer ${token}`);
          return fetch(url, { ...init, headers });
        },
      })
    : null;

  // Optional experiments emitter (A/B around tour completion)
  const EXP_ID = String(im.env?.VITE_TOUR_EXPERIMENT_ID || '');
  const EXP_VARIANTS = String(im.env?.VITE_TOUR_EXPERIMENT_VARIANTS || 'A,B')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const EXP_WEIGHTS = String(im.env?.VITE_TOUR_EXPERIMENT_WEIGHTS || '')
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => !Number.isNaN(n));
  const expEmitter = ENG_URL && EXP_ID ? new ExperimentsEmitter(ENG_URL, token) : null;
  const expAssignedRef = { current: false } as { current: boolean };
  const expVariantRef = { current: undefined as string | undefined };

  // Minimal sticky assignment (mirrors @digiwedge/tour-experiments)
  function assignBucket(
    experimentId: string,
    uid: string | null,
    variants: string[],
    weights?: number[],
  ): { experimentId: string; userId: string; variant: string } {
    const userId = uid ?? 'anon';
    let h = 2166136261 ^ (experimentId.length + userId.length);
    const seed = `${experimentId}|${userId}`;
    for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
    h ^= h << 13;
    h ^= h >>> 17;
    h ^= h << 5;
    const u = ((h >>> 0) % 1_000_000) / 1_000_000;
    if (!weights || weights.length !== variants.length) {
      const idx = Math.floor(u * variants.length) % variants.length;
      return { experimentId, userId, variant: variants[idx] };
    }
    const total = weights.reduce((a, b) => a + b, 0) || 1;
    let acc = 0;
    for (let i = 0; i < variants.length; i++) {
      acc += weights[i] / total;
      if (u <= acc) return { experimentId, userId, variant: variants[i] };
    }
    return { experimentId, userId, variant: variants[variants.length - 1] };
  }

  if (emitter) {
    const runStartRef = { current: 0 };
    type CoreEvent = { type: string } & Partial<{
      tourId: string;
      stepId: string;
      index: number;
      action: 'next' | 'back' | 'skip';
    }>;
    tourCore.on((evt) => {
      if (!consentOk) return;
      const mapType = (t: string) =>
        t.includes(':')
          ? (t.replace(':', '_') as TourEventIngest['type'])
          : (t as TourEventIngest['type']);
      const e = evt as CoreEvent;
      if (e.type === 'start') {
        runStartRef.current = performance.now();
        // Experiment assignment + exposure on first start
        if (expEmitter && EXP_ID && !expAssignedRef.current) {
          const assignment = assignBucket(
            EXP_ID,
            userId ?? null,
            EXP_VARIANTS.length > 0 ? EXP_VARIANTS : ['A', 'B'],
            EXP_WEIGHTS.length === EXP_VARIANTS.length ? EXP_WEIGHTS : undefined,
          );
          expVariantRef.current = assignment.variant;
          expEmitter.assigned({
            tenantId,
            experimentId: EXP_ID,
            variant: assignment.variant,
            userId: userId ?? undefined,
          });
          expEmitter.exposure({
            tenantId,
            experimentId: EXP_ID,
            variant: assignment.variant,
            userId: userId ?? undefined,
          });
          expAssignedRef.current = true;
        }
      }
      const ev: TourEventIngest = {
        clientEventId:
          (globalThis.crypto?.randomUUID && crypto.randomUUID()) ||
          String(Date.now() + Math.random()),
        tenantId,
        userId: userId ?? undefined,
        tourId: e.tourId || 'onboarding',
        version: 'v1',
        type: mapType(e.type),
        stepId: e.stepId ?? null,
        index: e.index ?? null,
        action: e.action ?? null,
        durationMs:
          e.type === 'finish' && runStartRef.current > 0
            ? Math.round(performance.now() - runStartRef.current)
            : null,
        consented: consentOk,
      };
      emitter.push(ev);

      // Bridge: mark experiment goal on tour completion
      if (e.type === 'finish' && expEmitter && EXP_ID && expVariantRef.current) {
        const dur =
          runStartRef.current > 0 ? Math.round(performance.now() - runStartRef.current) : undefined;
        expEmitter.goal({
          tenantId,
          experimentId: EXP_ID,
          variant: expVariantRef.current,
          userId: userId ?? undefined,
          goal: 'completed',
          durationMs: dur,
        });
        // Flush on completion to avoid losing event
        void expEmitter.flush();
      }
    });

    // Overlay perf: listen for first-paint and emit overlay_perf event
    useEffect(() => {
      const onPerf = (e: Event) => {
        const detail = (e as CustomEvent<{ ms: number }>).detail;
        if (!detail || !Number.isFinite(detail.ms)) return;
        const ev: TourEventIngest = {
          clientEventId:
            (globalThis.crypto?.randomUUID && crypto.randomUUID()) ||
            String(Date.now() + Math.random()),
          tenantId,
          userId: userId ?? undefined,
          tourId: 'overlay',
          version: 'v1',
          type: 'overlay_perf',
          durationMs: Math.max(0, Math.round(detail.ms)),
          consented: consentOk,
        };
        emitter.push(ev);
      };
      window.addEventListener('dw_tour_overlay_first_paint', onPerf as EventListener);
      return () =>
        window.removeEventListener('dw_tour_overlay_first_paint', onPerf as EventListener);
    }, [tenantId, userId, consentOk, emitter]);

    // Flush experiment events on page hide
    if (expEmitter) {
      window.addEventListener('pagehide', () => {
        void expEmitter.flush({ keepalive: true });
      });
    }
  }

  // TCF CoreModel wiring (flag-gated via VITE_TCF_ENABLE and region hint)
  try {
    installTcfCoreApiOnWindow(window as unknown as Window & typeof globalThis);
  } catch {
    /* noop */
  }
  useEffect(() => {
    try {
      const meta =
        (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
      const TCF_ON = String(meta?.VITE_TCF_ENABLE || '') === '1';
      const REGION = (getRegion() as string) || 'row';
      const isEU = REGION === 'eea-uk';
      if (!TCF_ON || !isEU) {
        setCoreModel(null);
        setPublisherModel(null);
        return;
      }
      const now = Date.now();
      const purposeConsents = {
        1: !!consentOk,
        2: false,
        3: false,
        4: false,
        5: false,
        6: false,
        7: false,
        8: false,
        9: false,
        10: false,
        11: false,
        12: false,
      } as unknown as Record<PurposeId, boolean>;
      const purposeLI = {
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
        6: false,
        7: false,
        8: false,
        9: false,
        10: false,
        11: false,
        12: false,
      } as unknown as Record<PurposeId, boolean>;
      const core: CoreModel = {
        created: now,
        lastUpdated: now,
        cmpId: 21,
        cmpVersion: 1,
        policyVersion: 4,
        gdprApplies: true,
        // Minimal mapping: purpose 1 only; extend once CMP UI maps full purposes
        purposeConsents,
        purposeLI,
        specialFeatures: { 1: false, 2: false },
        vendorConsents: new Set<number>(),
        vendorLI: new Set<number>(),
      };
      setCoreModel(core);
      // Publisher (EU-only). Mirror the minimal mapping used above.
      const pub: PublisherModel = {
        pubPurposesConsent: {
          ...purposeConsents,
        } as unknown as PublisherModel['pubPurposesConsent'],
        pubPurposesLITransparency: {
          ...purposeLI,
        } as unknown as PublisherModel['pubPurposesLITransparency'],
      };
      setPublisherModel(pub);
    } catch {
      /* noop */
    }
  }, [consentOk]);

  // Emit strings to registry when consent changes (soft-fail)
  useEffect(() => {
    try {
      const env =
        (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
      const REG_URL = String(env?.VITE_CMP_REGISTRY_URL || '');
      if (!REG_URL) return;
      const SITE_ID = String(env?.VITE_CMP_SITE_ID || '');
      const regionLabel = String(env?.VITE_TCF_REGION || 'row').toLowerCase();
      const region: 'eea-uk' | 'us' | 'us-ca' | 'row' =
        regionLabel === 'eea-uk'
          ? 'eea-uk'
          : regionLabel === 'us-ca'
            ? 'us-ca'
            : regionLabel === 'us'
              ? 'us'
              : 'row';
      const gpc =
        (navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl ?? null;
      const bearer = token || undefined;
      void collectAndEmitConsent({
        baseUrl: REG_URL,
        bearer,
        region,
        gpc,
        siteId: SITE_ID || undefined,
      });
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentOk]);

  // Kick off tour at bootstrap (after we have profile + token). Preview token supported via query param.
  try {
    const meta = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
    const REG_URL = String(meta?.VITE_TOUR_REGISTRY_URL || '');
    const envLabel = String(meta?.VITE_ENV_LABEL || 'dev').toLowerCase();
    const env: 'dev' | 'stage' | 'prod' = envLabel.startsWith('prod')
      ? 'prod'
      : envLabel.startsWith('stage')
        ? 'stage'
        : 'dev';
    const qp = new URL(window.location.href).searchParams;
    const previewToken = qp.get('tourPreviewToken') ?? undefined;
    if (REG_URL && token && tenantId) {
      // Derive real traits for targeting
      const rolesUnknown = (profile as { roles?: unknown })?.roles;
      const role =
        Array.isArray(rolesUnknown) && typeof rolesUnknown[0] === 'string'
          ? (rolesUnknown[0] as string)
          : typeof (profile as { role?: unknown })?.role === 'string'
            ? ((profile as { role?: unknown }).role as string)
            : undefined;
      const ffUnknown = (profile as { featureFlags?: unknown })?.featureFlags;
      const featureFlagsArray = Array.isArray(ffUnknown)
        ? (ffUnknown as unknown[]).filter((x): x is string => typeof x === 'string')
        : undefined;
      const flagsMap = featureFlagsArray?.reduce<Record<string, boolean>>((acc, k) => {
        acc[k] = true;
        return acc;
      }, {}) as Record<string, boolean> | undefined;
      // Persist firstSeenVersion once per browser profile
      const firstSeenVersion = (() => {
        try {
          const key = 'portal.firstSeenVersion';
          let v = localStorage.getItem(key);
          if (!v) {
            const appVersion = String(meta?.VITE_APP_VERSION || '0.0.0');
            v = appVersion;
            localStorage.setItem(key, v);
          }
          return v || undefined;
        } catch {
          return String(meta?.VITE_APP_VERSION || '0.0.0');
        }
      })();
      const planUnknown =
        (profile as { plan?: unknown; billingPlan?: unknown; tier?: unknown })?.plan ??
        ((profile as Record<string, unknown>)?.['billingPlan'] as unknown) ??
        ((profile as Record<string, unknown>)?.['tier'] as unknown);
      const plan = typeof planUnknown === 'string' ? planUnknown : undefined;
      void maybeRunTour({
        baseUrl: REG_URL,
        tenantId,
        env,
        tourId: 'onboarding',
        authToken: token,
        traits: {
          userId: profile?.userId ?? undefined,
          role,
          plan,
          flags: flagsMap,
          firstSeenVersion,
        },
        storagePrefix: 'portal.',
        previewToken,
      });
    }
  } catch {
    /* noop */
  }

  return null;
}

function PortalApp() {
  const consentOk = useProductAnalyticsConsent();

  return (
    <>
      {consentOk ? <DomPortalForTour /> : null}
      {/* Analytics wiring with real identity/consent */}
      <TourAnalyticsWire consentOk={consentOk} />
      <PreviewBadge />
      <DevFlagsBanner />
      <PortalI18nProvider>
        <PortalProviders>
          <ErrorBoundary>
            <BrowserRouter>
              <AppBoot>
                <Routes>
                  {/* Public pages with shell */}
                  <Route element={<PublicShell />}>
                    <Route index element={<HomePage />} />
                    <Route path="/home" element={<HomePage />} />
                    <Route path="/scanner" element={<ScannerPage />} />
                    <Route path="/health" element={<HealthPage />} />
                  </Route>
                  {/* Auth pages (public-only) */}
                  <Route element={<PublicOnly />}>
                    <Route path="/login" element={<LoginRedirect />} />
                    <Route path="/signup" element={<Register appName="CMP Portal" />} />
                  </Route>

                  {/* Protected + nested */}
                  <Route element={<Protected />}>
                    <Route element={<AppShell />}>
                      <Route index element={<Navigate to="/sites" replace />} />
                      <Route path="/sites" element={<SitesPageWrapper />} />
                      <Route path="/analytics" element={<AnalyticsPageWrapper />} />
                      <Route path="/overrides" element={<OverridesPageWrapper />} />
                      <Route path="/export" element={<ExportsPageWrapper />} />
                      <Route path="/scans" element={<ScansPageWrapper />} />
                      <Route path="/config" element={<ConfigEditorWrapper />} />
                      <Route path="/config/edit" element={<ConfigEditorWrapper />} />
                      {FLAGS.THEME_EDITOR && (
                        <Route path="/admin/themes" element={<ThemeEditorPage />} />
                      )}
                      <Route path="/diagnostics" element={<AdminDiagnostics />} />
                    </Route>
                  </Route>
                  <Route path="/403" element={<Forbidden />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </AppBoot>
            </BrowserRouter>
          </ErrorBoundary>
        </PortalProviders>
      </PortalI18nProvider>
    </>
  );
}

createRoot(rootEl).render(
  <StrictMode>
    <PortalApp />
  </StrictMode>,
);
